import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  ApproverItem,
  fetchSelfTransferDetails,
  formatDateDisplay,
  groundLabel,
  parseApprovers,
  reasonLabel,
  resolveApproverStatus,
  resolveStatusBadge,
} from "./selfTransfer.service";

const SelfTransferApprovers: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id: routeId } = useParams<{ id: string }>();
  const location = useLocation();

  const stateItem = (location.state as any)?.item || null;
  const requestId = routeId || stateItem?.RequestID || stateItem?.referenceNumber || stateItem?.id;

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<any>(stateItem);
  const [approvers, setApprovers] = useState<ApproverItem[]>([]);

  useEffect(() => {
    if (!requestId) return;

    // If passed via navigation state, extract approvers immediately
    if (stateItem) {
      setDetails(stateItem);
      const parsed = parseApprovers(stateItem);
      setApprovers(parsed);
    }

    // Always fetch fresh details from API
    const loadFresh = async () => {
      try {
        setLoading(true);
        const res = await fetchSelfTransferDetails(String(requestId));
        if (res.success && res.data) {
          setDetails(res.data);
          const parsed = parseApprovers(res.data);
          setApprovers(parsed);
        } else if (!stateItem) {
          toast.warning("Could not load approver details from server");
        }
      } catch (err: any) {
        if (!stateItem) {
          toast.error(err?.message || "Failed to load approvers");
        }
      } finally {
        setLoading(false);
      }
    };

    loadFresh();
  }, [requestId, stateItem]);

  const reqId = details?.RequestID || details?.referenceNumber || details?.id || requestId || "-";
  const ground = groundLabel(details?.TransferGround);
  const reason = reasonLabel(details?.ReasonForTransfer);
  const createdDate = formatDateDisplay(details?.created_at || details?.createOn);
  const overallStatus = details?.Status || details?.status || "INPROCESS";
  const overallBadge = resolveStatusBadge(overallStatus);

  const getStepStatusConfig = (rawStatus?: string, isPassedDown: boolean = true) => {
    const s = String(rawStatus || "").trim().toUpperCase();
    if (["APPROVE", "APPROVED", "A"].includes(s)) {
      return {
        label: "Approved",
        color: "#28a745",
        cardBorder: "#28a745",
        bg: "rgba(40, 167, 69, 0.1)",
        border: "#86efac",
        icon: "ti ti-circle-check",
        badgeClass: "badge bg-success-subtle text-success border border-success-subtle",
      };
    }
    if (["REJECT", "REJECTED", "R"].includes(s)) {
      return {
        label: "Rejected",
        color: "#dc3545",
        cardBorder: "#dc3545",
        bg: "rgba(220, 53, 69, 0.1)",
        border: "#fca5a5",
        icon: "ti ti-circle-x",
        badgeClass: "badge bg-danger-subtle text-danger border border-danger-subtle",
      };
    }
    if (s === "PENDING" || !isPassedDown) {
      return {
        label: "Pending",
        color: "#94a3b8",
        cardBorder: "#cbd5e1",
        bg: "rgba(148, 163, 184, 0.1)",
        border: "#cbd5e1",
        icon: "ti ti-clock",
        badgeClass: "badge bg-light text-muted border border-secondary-subtle",
      };
    }
    return {
      label: "In Process",
      color: "#f39c12",
      cardBorder: "#f39c12",
      bg: "rgba(243, 156, 18, 0.1)",
      border: "#fde68a",
      icon: "ti ti-clock-hour-4",
      badgeClass: "badge bg-warning-subtle text-warning-emphasis border border-warning-subtle",
    };
  };

  return (
    <div className="page-wrapper" style={{ backgroundColor: "#f8f9fa" }}>
      <div className="content">
        {/* Page Header (Exact DMRC Web Header Style) */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="btn btn-white border d-flex align-items-center justify-content-center shadow-sm me-3 text-secondary"
                style={{ width: "40px", height: "40px", borderRadius: "50%" }}
                onClick={() => navigate(-1)}
                title="Back to Previous Page"
              >
                <i className="ti ti-arrow-left" style={{ fontSize: "18px" }} />
              </button>
              <div>
                <h3 className="page-title mb-1 fw-bold text-dark">Approver Workflow & Timeline</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.employeeDashboard}>Home</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to={routes.selfTransfer.list}>Self Transfer</Link>
                    </li>
                    <li className="breadcrumb-item active text-danger">Workflow Timeline</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link
              to={routes.selfTransfer.list}
              className="btn btn-outline-secondary rounded-pill px-3 shadow-sm border-2 fw-medium d-flex align-items-center gap-1"
            >
              <i className="ti ti-list" />
              All Requests
            </Link>
          </div>
        </div>

        {/* Top Request Summary Card */}
        <div className="card border shadow-sm mb-4" style={{ borderLeft: "4px solid #ab1f24" }}>
          <div className="card-body p-3.5 px-4">
            <div className="row g-3 align-items-center">
              <div className="col-lg-3 col-md-6">
                <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-1">
                  Reference Number
                </span>
                <span className="fw-bold text-dark font-monospace fs-14">
                  {reqId}
                </span>
              </div>

              <div className="col-lg-3 col-md-6">
                <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-1">
                  Transfer Details
                </span>
                <div className="fs-13 fw-bold text-dark">
                  {ground} <span className="text-muted fw-normal">({reason})</span>
                </div>
              </div>

              <div className="col-lg-3 col-md-6">
                <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-1">
                  Application Date
                </span>
                <div className="fs-13 fw-bold text-dark d-flex align-items-center gap-1">
                  <i className="ti ti-calendar text-muted" />
                  {createdDate}
                </div>
              </div>

              <div className="col-lg-3 col-md-6 text-lg-end">
                <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-1">
                  Overall Status
                </span>
                <span
                  className="badge px-2.5 py-1 fs-11 fw-semibold d-inline-flex align-items-center"
                  style={{
                    color: overallBadge.text,
                    backgroundColor: overallBadge.bg,
                    border: `1px solid ${overallBadge.border}`,
                  }}
                >
                  <span
                    className="d-inline-block rounded-circle me-1.5"
                    style={{ width: 6, height: 6, backgroundColor: overallBadge.dotColor }}
                  />
                  {overallBadge.label}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Workflow Approvers Content */}
        {loading && approvers.length === 0 ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" style={{ color: "#ab1f24" }} role="status" />
            <p className="text-muted mt-2 small fw-medium">Loading approvers workflow...</p>
          </div>
        ) : approvers.length === 0 ? (
          <div className="card border shadow-sm text-center py-5">
            <div className="card-body text-muted">
              <i className="ti ti-users-minus fs-1 d-block mb-2 text-secondary opacity-50" />
              <h6 className="fw-bold">No approver stages assigned yet</h6>
              <p className="small text-muted mb-3">This request is currently queued for initial workflow dispatch.</p>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary px-3"
                onClick={() => navigate(-1)}
              >
                Back to Request
              </button>
            </div>
          </div>
        ) : (
          <div className="card border shadow-sm mb-4">
            <div className="card-header bg-light py-3 px-4 border-bottom d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <i className="ti ti-git-branch text-danger fs-5" />
                <h6 className="card-title mb-0 fw-bold text-dark">
                  Multi-Level Approval Flow ({approvers.length} {approvers.length === 1 ? "Stage" : "Stages"})
                </h6>
              </div>
              <span className="badge bg-white text-muted border px-2 py-0.5 text-uppercase fs-11 fw-semibold">
                DMRC Cadre Hierarchy
              </span>
            </div>

            <div className="card-body p-4">
              <div className="timeline-workflow">
                {approvers.map((item, index) => {
                  const isPassedDown =
                    index === 0 ||
                    approvers.slice(0, index).every((prev) => {
                      const ps = String(prev.status || "").trim().toUpperCase();
                      return ["APPROVE", "APPROVED", "A"].includes(ps);
                    });
                  const cfg = getStepStatusConfig(item.status, isPassedDown);
                  const isLast = index === approvers.length - 1;
                  const isActed = cfg.label === "Approved" || cfg.label === "Rejected";

                  const rawRemark = item.remark ? String(item.remark).trim() : "";
                  const hasRemark =
                    isActed &&
                    rawRemark !== "" &&
                    rawRemark !== "-" &&
                    rawRemark !== "—" &&
                    rawRemark.toLowerCase() !== "null" &&
                    rawRemark.toLowerCase() !== "undefined";

                  const rawDate = item.date ? String(item.date).trim() : "";
                  const hasActionDate =
                    isActed &&
                    rawDate !== "" &&
                    rawDate !== "-" &&
                    rawDate !== "—" &&
                    rawDate.toLowerCase() !== "pending" &&
                    rawDate.toLowerCase() !== "null" &&
                    rawDate.toLowerCase() !== "undefined";

                  const showActionDate = hasRemark && hasActionDate;

                  return (
                    <div key={item.id || index} className="d-flex position-relative mb-4">
                      {/* Step Number & Vertical Timeline Line */}
                      <div className="d-flex flex-column align-items-center me-3.5" style={{ width: "38px" }}>
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-white flex-shrink-0"
                          style={{
                            width: "36px",
                            height: "36px",
                            backgroundColor: cfg.color,
                            fontSize: "13px",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                          }}
                        >
                          <i className={cfg.icon} style={{ fontSize: "16px" }} />
                        </div>
                        {!isLast && (
                          <div
                            style={{
                              width: "2px",
                              flexGrow: 1,
                              minHeight: "48px",
                              backgroundColor: "#CBD5E1",
                              margin: "8px 0",
                            }}
                          />
                        )}
                      </div>

                      {/* Step Card Content */}
                      <div className="flex-grow-1">
                        <div
                          className="card border shadow-xs mb-0"
                          style={{
                            borderLeft: `4px solid ${cfg.cardBorder || cfg.color}`,
                          }}
                        >
                          <div className="card-body p-3.5 px-4">
                            {/* Header: Approver Name, Level, Status */}
                            <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
                              <div>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge bg-light text-dark border px-2 py-0.5 fs-11 fw-bold">
                                    Level {item.level}
                                  </span>
                                  <h6 className="fw-bold text-dark mb-0 fs-14">
                                    {item.name}
                                  </h6>
                                </div>
                                <div className="text-muted fs-12 mt-1">
                                  Employee Code: <strong className="text-dark font-monospace">{item.employeeCode}</strong>
                                </div>
                              </div>

                              <div className="text-end">
                                <span
                                  className="badge px-2.5 py-1 fs-11 fw-semibold"
                                  style={{
                                    color: cfg.color === "#94a3b8" ? "#64748b" : cfg.color,
                                    backgroundColor: cfg.bg,
                                    border: `1px solid ${cfg.border}`,
                                  }}
                                >
                                  <i className={`${cfg.icon} me-1 fs-11`} />
                                  {cfg.label}
                                </span>
                              </div>
                            </div>

                            {/* Metadata Row: Hide Action Date if user didn't remark */}
                            <div className="row g-3 py-2.5 border-top border-bottom my-3 fs-13">
                              <div className={showActionDate ? "col-sm-6" : "col-12"}>
                                <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-0.5">Stage Role:</span>
                                <span className="text-dark fw-bold">
                                  {item.level === "1" ? "Reporting HOD / Unit Head" : `Approving Authority (L${item.level})`}
                                </span>
                              </div>
                              {showActionDate && (
                                <div className="col-sm-6">
                                  <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-0.5">Action Date:</span>
                                  <span className="text-dark fw-bold d-flex align-items-center gap-1">
                                    <i className="ti ti-calendar text-muted" />
                                    {item.date}
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Remarks: Only shown if user remarked (no blank box, no placeholder) */}
                            {hasRemark && (
                              <div className="border-start border-3 border-danger-subtle ps-3 py-2 text-dark fs-12 bg-light-subtle rounded-end mt-2">
                                <strong className="text-muted">Remarks:</strong> {item.remark}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelfTransferApprovers;
